import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import KeyIcon from '@mui/icons-material/Key';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import Footer from '../Footer';

const TOKENS = {
  secondary: '#3B82F6',
  accent: '#D97706',
  accentGlow: 'rgba(217, 119, 6, 0.35)',
  bgDark: '#0B132B',
  bgCard: 'rgba(15, 23, 42, 0.85)',
  border: 'rgba(219, 234, 254, 0.15)',
  borderActive: 'rgba(59, 130, 246, 0.5)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textHighlight: '#60A5FA',
  greenSignal: '#10B981',
};

const PLUGINS = [
  {
    id: 'digipin',
    to: '/digipin-encoder',
    issuesTo: '/digipin-encoder/issues',
    icon: <PinDropOutlinedIcon sx={{ fontSize: 32, color: TOKENS.accent }} />,
    badge: '4m Grid',
    badgeColor: TOKENS.accent,
    title: 'DIGIPIN Encoder Pro',
    subtitle: 'National geo-spatial pin standard for QGIS',
    description:
      'Encode and decode India Post DIGIPIN codes at ~4m × 4m resolution. Click-to-encode on the map, batch attribute tables, and run online or offline with a GeoVera API key.',
    highlights: [
      'Map click encoder & decode-to-zoom',
      'Batch layer attribute encoding',
      'Cloud API key + offline hybrid mode',
      'QGIS 3.16+ compatible',
    ],
    cta: 'Open DIGIPIN Plugin',
    secondaryCta: 'Report issues',
  },
  {
    id: 'cliptocad',
    to: '/cliptocad',
    issuesTo: '/cliptocad/issues',
    icon: <ContentCopyIcon sx={{ fontSize: 32, color: TOKENS.secondary }} />,
    badge: 'CAD Bridge',
    badgeColor: TOKENS.secondary,
    title: 'ClipToCAD',
    subtitle: 'QGIS → AutoCAD clipboard bridge',
    description:
      'Copy selected QGIS features and paste them into AutoCAD as _PLINE / _POINT / _TEXT. Multi-layer selections, projected metres, unit presets, and auto parcel-ID labels. QGIS 3.16–4.x.',
    highlights: [
      'Selection-only clipboard transfer (no whole-layer dump)',
      'Real World metres (LT-safe) and Local AutoLISP units',
      'Auto parcel-ID labels — FID is never copied',
      'QGIS 3.16–4.x (Qt5 and Qt6)',
    ],
    cta: 'Open ClipToCAD Plugin',
    secondaryCta: 'Support & FAQ',
  },
] as const;

const SHARED = [
  {
    icon: <ExtensionOutlinedIcon sx={{ color: TOKENS.textHighlight }} />,
    title: 'Native QGIS plugins',
    body: 'Install from the QGIS Plugin Manager, or copy the plugin folder into your profile — no separate desktop app.',
  },
  {
    icon: <ArchitectureIcon sx={{ color: TOKENS.accent }} />,
    title: 'Survey & drafting ready',
    body: 'Built for land records, utilities, and CAD handoff workflows in the field and office.',
  },
  {
    icon: <KeyIcon sx={{ color: TOKENS.greenSignal }} />,
    title: 'Tied to GeoVera cloud',
    body: 'Optional API keys and developer portal for high-throughput DIGIPIN encoding online.',
  },
] as const;

const QgisPluginsPage = () => {
  return (
    <Box sx={{ ...publicPageSx, bgcolor: TOKENS.bgDark, color: TOKENS.textPrimary }}>
      <PublicSiteChrome active="plugins" subtitle="QGIS Plugins" />

      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 8, md: 12 } }}>
        {/* Hero */}
        <Stack spacing={2.5} sx={{ maxWidth: 760, mb: { xs: 5, md: 8 } }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Chip
              label="QGIS PLUGIN SUITE"
              size="small"
              sx={{
                bgcolor: 'rgba(217, 119, 6, 0.18)',
                color: TOKENS.accent,
                border: `1px solid ${TOKENS.accentGlow}`,
                fontWeight: 800,
                fontSize: 10,
                letterSpacing: '0.08em',
              }}
            />
            <Chip
              label="QGIS 3.16–4.x"
              size="small"
              sx={{
                bgcolor: 'rgba(59, 130, 246, 0.15)',
                color: TOKENS.textHighlight,
                fontSize: 10,
                fontWeight: 700,
              }}
            />
          </Stack>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2.1rem', sm: '2.75rem', md: '3.25rem' },
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            QGIS Plugins
          </Typography>

          <Typography
            sx={{
              color: TOKENS.textSecondary,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              lineHeight: 1.65,
              maxWidth: 640,
            }}
          >
            Extend QGIS with GeoVera&apos;s production plugins — national DIGIPIN geo-pins and a
            one-click ClipToCAD bridge into AutoCAD. Install, configure, and ship survey-ready data.
          </Typography>
        </Stack>

        {/* Plugin cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 3, md: 4 },
            mb: { xs: 6, md: 10 },
          }}
        >
          {PLUGINS.map((plugin) => (
            <Paper
              key={plugin.id}
              elevation={0}
              sx={{
                p: { xs: 3, sm: 3.5 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                '&:hover': {
                  borderColor: TOKENS.borderActive,
                  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: 'rgba(30, 58, 138, 0.35)',
                    border: `1px solid ${TOKENS.border}`,
                    display: 'flex',
                    flexShrink: 0,
                  }}
                >
                  {plugin.icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Chip
                    label={plugin.badge}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 800,
                      mb: 0.75,
                      bgcolor: `${plugin.badgeColor}22`,
                      color: plugin.badgeColor,
                      border: `1px solid ${plugin.badgeColor}55`,
                    }}
                  />
                  <Typography
                    sx={{ fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.02em' }}
                  >
                    {plugin.title}
                  </Typography>
                  <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13, mt: 0.25 }}>
                    {plugin.subtitle}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                sx={{ color: TOKENS.textSecondary, fontSize: '0.95rem', lineHeight: 1.6, mb: 2.5 }}
              >
                {plugin.description}
              </Typography>

              <Stack spacing={1} sx={{ mb: 3, flex: 1 }}>
                {plugin.highlights.map((item) => (
                  <Stack key={item} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 18, color: TOKENS.greenSignal, mt: 0.15, flexShrink: 0 }}
                    />
                    <Typography
                      sx={{ fontSize: 13.5, color: TOKENS.textPrimary, lineHeight: 1.45 }}
                    >
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  component={RouterLink}
                  to={plugin.to}
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: TOKENS.accent,
                    color: '#FFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2.5,
                    py: 1.1,
                    boxShadow: `0 6px 18px ${TOKENS.accentGlow}`,
                    '&:hover': { bgcolor: '#B45309' },
                  }}
                >
                  {plugin.cta}
                </Button>
                <Button
                  component={RouterLink}
                  to={plugin.issuesTo}
                  variant="outlined"
                  sx={{
                    borderColor: TOKENS.border,
                    color: TOKENS.textPrimary,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    '&:hover': {
                      borderColor: TOKENS.secondary,
                      bgcolor: 'rgba(59, 130, 246, 0.08)',
                    },
                  }}
                >
                  {plugin.secondaryCta}
                </Button>
              </Stack>
            </Paper>
          ))}
        </Box>

        {/* Shared value props */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
            gap: 2.5,
            mb: { xs: 6, md: 8 },
          }}
        >
          {SHARED.map((item) => (
            <Paper
              key={item.title}
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: 'rgba(15, 23, 42, 0.55)',
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 2.5,
              }}
            >
              <Box sx={{ mb: 1.25 }}>{item.icon}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.75 }}>{item.title}</Typography>
              <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13, lineHeight: 1.55 }}>
                {item.body}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Bottom CTA */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: `1px solid ${TOKENS.borderActive}`,
            bgcolor: 'rgba(30, 58, 138, 0.2)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, mb: 0.75 }}
            >
              Need a DIGIPIN cloud key?
            </Typography>
            <Typography sx={{ color: TOKENS.textSecondary, fontSize: 14, maxWidth: 480 }}>
              Sign in to claim or manage API keys for high-speed online encoding in DIGIPIN Encoder
              Pro.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/developers/digipin"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              flexShrink: 0,
              bgcolor: TOKENS.secondary,
              color: '#FFF',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              py: 1.15,
              '&:hover': { bgcolor: '#2563EB' },
            }}
          >
            Developer portal
          </Button>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default QgisPluginsPage;
